import type { GridFilterItem } from '@mui/x-data-grid';
import moment from 'moment';

import type {
  BooleanFilterValue,
  DateRangeFilterValue,
  EnumFilterValue,
  ExistenceFilterValue,
  FilterFieldDef,
  FilterRow,
  NumericRangeFilterValue,
  TextSearchFilterValue,
  UserReferenceFilterValue,
} from './smartFilterTypes';
import type { ActiveFilter, FilterOption } from './types';

/** UI identity of a field def. Defaults to backendField; set `id` when two defs share a field. */
const defId = (d: FilterFieldDef): string => d.id ?? d.backendField;

/**
 * Operators each field type emits. Used to route GridFilterItems back to the right def when
 * several defs share a backendField (e.g. dateRange vs existence on `CRMUpdateAt`). Defs that
 * share a field MUST have disjoint operator sets here, or routing is ambiguous.
 */
const TYPE_OPERATORS: Record<FilterFieldDef['type'], string[]> = {
  dateRange: ['greaterThan', 'lessThan'],
  numericRange: ['greaterThan', 'lessThan', 'equals', 'notEquals'],
  existence: ['isNotEmpty', 'isEmpty'],
  boolean: ['equals'],
  enum: ['in'],
  userReference: ['equals'],
  textSearch: ['contains'],
  numberList: ['isAnyOf'],
};

/** Items belonging to a def: same backend field AND an operator the def's type produces. */
function itemsForDef(items: GridFilterItem[], def: FilterFieldDef): GridFilterItem[] {
  const ops = TYPE_OPERATORS[def.type];
  return items.filter((i) => i.field === def.backendField && ops.includes(i.operator));
}

// ── FilterRow → GridFilterItem[] ─────────────────────────────────────────────

/** Convert a single UI FilterRow into 0–2 GridFilterItems the backend understands. */
export function filterRowToGridItems(row: FilterRow, fieldDef: FilterFieldDef): GridFilterItem[] {
  const field = fieldDef.backendField;

  switch (fieldDef.type) {
    case 'dateRange': {
      const val = row.value as DateRangeFilterValue | null;
      if (!val) return [];
      const items: GridFilterItem[] = [];
      if (val.start) items.push({ field, operator: 'greaterThan', value: val.start });
      if (val.end) items.push({ field, operator: 'lessThan', value: val.end });
      return items;
    }

    case 'boolean': {
      const val = row.value as BooleanFilterValue;
      if (val == null) return [];
      return [{ field, operator: 'equals', value: String(val) }];
    }

    case 'enum': {
      const val = row.value as EnumFilterValue | null;
      if (!val || val.length === 0) return [];
      return [{ field, operator: 'in', value: val.join(',') }];
    }

    case 'userReference': {
      const val = row.value as UserReferenceFilterValue;
      if (!val) return [];
      return [{ field, operator: 'equals', value: val }];
    }

    case 'textSearch': {
      const val = row.value as TextSearchFilterValue | null;
      if (!val) return [];
      return [{ field, operator: 'contains', value: val }];
    }

    case 'numericRange': {
      const val = row.value as NumericRangeFilterValue | null;
      if (!val) return [];
      const items: GridFilterItem[] = [];
      if (val.min != null) items.push({ field, operator: 'greaterThan', value: String(val.min) });
      if (val.max != null) items.push({ field, operator: 'lessThan', value: String(val.max) });
      return items;
    }

    case 'existence': {
      const val = row.value as ExistenceFilterValue;
      if (val == null) return [];
      return [{ field, operator: val ? 'isNotEmpty' : 'isEmpty', value: '' }];
    }

    case 'numberList': {
      const val = row.value as number[] | null;
      if (!val || val.length === 0) return [];
      return [{ field, operator: 'isAnyOf', value: val }];
    }

    default:
      return [];
  }
}

/** Convert all rows to a flat array of GridFilterItems. */
export function filterRowsToGridItems(rows: FilterRow[], fieldDefs: FilterFieldDef[]): GridFilterItem[] {
  const defMap = new Map(fieldDefs.map((d) => [defId(d), d]));
  const items: GridFilterItem[] = [];
  for (const row of rows) {
    if (!row.fieldId) continue;
    const def = defMap.get(row.fieldId);
    if (!def) continue;
    items.push(...filterRowToGridItems(row, def));
  }
  return items;
}

// ── GridFilterItem[] → FilterRow[] ───────────────────────────────────────────

/** Parse existing GridFilterItems back into FilterRows for the UI (e.g. when reopening the modal). */
export function gridItemsToFilterRows(items: GridFilterItem[], fieldDefs: FilterFieldDef[]): FilterRow[] {
  const rows: FilterRow[] = [];
  const newId = () => crypto.randomUUID();

  // Iterate defs (not item.field) so two defs sharing a backendField each claim only their
  // own operators (e.g. dateRange takes gt/lt, existence takes isEmpty/isNotEmpty).
  for (const def of fieldDefs) {
    const field = defId(def);
    const fieldItems = itemsForDef(items, def);
    if (fieldItems.length === 0) continue;

    switch (def.type) {
      case 'dateRange': {
        const start = (fieldItems.find((i) => i.operator === 'greaterThan')?.value as string | undefined) ?? null;
        const end = (fieldItems.find((i) => i.operator === 'lessThan')?.value as string | undefined) ?? null;
        if (start || end) {
          rows.push({ id: newId(), fieldId: field, value: { start, end } });
        }
        break;
      }

      case 'boolean': {
        const item = fieldItems[0];
        if (item) {
          rows.push({ id: newId(), fieldId: field, value: item.value === 'true' });
        }
        break;
      }

      case 'enum': {
        const item = fieldItems.find((i) => i.operator === 'in');
        if (item && typeof item.value === 'string' && item.value.length > 0) {
          rows.push({ id: newId(), fieldId: field, value: item.value.split(',') });
        }
        break;
      }

      case 'userReference': {
        const item = fieldItems[0];
        if (item?.value) {
          rows.push({ id: newId(), fieldId: field, value: String(item.value) });
        }
        break;
      }

      case 'textSearch': {
        const item = fieldItems[0];
        if (item?.value) {
          rows.push({ id: newId(), fieldId: field, value: String(item.value) });
        }
        break;
      }

      case 'numericRange': {
        const min = fieldItems.find((i) => i.operator === 'greaterThan')?.value;
        const max = fieldItems.find((i) => i.operator === 'lessThan')?.value;
        if (min != null || max != null) {
          rows.push({
            id: newId(),
            fieldId: field,
            value: {
              min: min != null ? Number(min) : null,
              max: max != null ? Number(max) : null,
            },
          });
        }
        break;
      }

      case 'existence': {
        const item = fieldItems[0];
        if (item) {
          rows.push({ id: newId(), fieldId: field, value: item.operator === 'isNotEmpty' });
        }
        break;
      }

      case 'numberList': {
        const item = fieldItems.find((i) => i.operator === 'isAnyOf');
        if (item && Array.isArray(item.value) && item.value.length > 0) {
          rows.push({ id: newId(), fieldId: field, value: item.value });
        }
        break;
      }
    }
  }

  return rows;
}

// ── GridFilterItem[] → ActiveFilter[] ────────────────────────────────────────

const DATE_FMT = 'DD/MM/YYYY';

function resolveLabel(value: string, options?: FilterOption[]): string {
  return options?.find((o) => o.value === value)?.label ?? value;
}

/**
 * Converts a flat array of GridFilterItems into ActiveFilter chips.
 * Range-type fields (dateRange, numericRange) are merged into a single chip.
 */
export function gridItemsToActiveFilters(
  items: GridFilterItem[],
  fieldDefs: FilterFieldDef[],
  dynamicOptions?: Record<string, FilterOption[]>
): ActiveFilter[] {
  const chips: ActiveFilter[] = [];

  for (const def of fieldDefs) {
    const field = defId(def);
    const fieldItems = itemsForDef(items, def);
    if (fieldItems.length === 0) continue;

    const options = def.options ?? (def.dynamicOptionsKey ? dynamicOptions?.[def.dynamicOptionsKey] : undefined);

    let displayValue = '';

    switch (def.type) {
      case 'dateRange': {
        const start = fieldItems.find((i) => i.operator === 'greaterThan')?.value as string | undefined;
        const end = fieldItems.find((i) => i.operator === 'lessThan')?.value as string | undefined;
        if (start && end) {
          displayValue = `${moment(start).format(DATE_FMT)} – ${moment(end).format(DATE_FMT)}`;
        } else if (start) {
          displayValue = `desde ${moment(start).format(DATE_FMT)}`;
        } else if (end) {
          displayValue = `hasta ${moment(end).format(DATE_FMT)}`;
        }
        break;
      }

      case 'boolean': {
        const val = fieldItems[0]?.value;
        displayValue = val === 'true' ? 'Sí' : 'No';
        break;
      }

      case 'enum': {
        const raw = fieldItems.find((i) => i.operator === 'in')?.value as string | undefined;
        if (raw) {
          displayValue = raw
            .split(',')
            .map((v) => resolveLabel(v, options))
            .join(', ');
        }
        break;
      }

      case 'userReference': {
        const val = String(fieldItems[0]?.value ?? '');
        displayValue = resolveLabel(val, options);
        break;
      }

      case 'textSearch': {
        displayValue = String(fieldItems[0]?.value ?? '');
        break;
      }

      case 'numericRange': {
        const min = fieldItems.find((i) => i.operator === 'greaterThan')?.value;
        const max = fieldItems.find((i) => i.operator === 'lessThan')?.value;
        const eq = fieldItems.find((i) => i.operator === 'equals')?.value;
        const neq = fieldItems.find((i) => i.operator === 'notEquals')?.value;
        if (min != null && max != null) {
          displayValue = `${min} – ${max}`;
        } else if (min != null) {
          displayValue = `≥ ${min}`;
        } else if (max != null) {
          displayValue = `≤ ${max}`;
        } else if (eq != null) {
          // Dashboard Sankey deep-links serialize the "category" filter as equals/notEquals
          // on count fields (e.g. messagesCount = 0 → Pendientes / Fallidos bucket).
          displayValue = `= ${eq}`;
        } else if (neq != null) {
          displayValue = `≠ ${neq}`;
        }
        break;
      }

      case 'existence': {
        displayValue = fieldItems[0]?.operator === 'isNotEmpty' ? 'Tiene valor' : 'Está vacío';
        break;
      }

      case 'numberList': {
        const item = fieldItems.find((i) => i.operator === 'isAnyOf');
        if (item && Array.isArray(item.value)) {
          displayValue = item.value.length.toString();
        }
        break;
      }
    }

    if (displayValue) {
      chips.push({ key: field, label: def.label, displayValue });
    }
  }

  return chips;
}

// ── Chip removal ─────────────────────────────────────────────────────────────

/**
 * Remove the items behind a chip. The chip `key` is a def id (see {@link defId}), so we resolve
 * the def and drop only items matching its field AND its operator set — leaving a sibling def on
 * the same backendField (e.g. dateRange vs existence on `CRMUpdateAt`) untouched.
 */
export function removeChipItems(
  items: GridFilterItem[],
  fieldDefs: FilterFieldDef[],
  chipKey: string
): GridFilterItem[] {
  const def = fieldDefs.find((d) => defId(d) === chipKey);
  if (!def) return items.filter((i) => i.field !== chipKey);
  const drop = new Set(itemsForDef(items, def));
  return items.filter((i) => !drop.has(i));
}
